const AbstractResourceController = require("../common/AbstractResourceController");
const DynAppsRenderer = require("./DynAppsRenderer");
const DynAppsListView = require("./DynAppsListView");
const Utils = require("../common/Utils");

/**
 * Controller for Dynamic Apps functionality
 *
 * @class
 * @extends {AbstractResourceController}
 * @constructor
 */
function DynAppsController(launchDialog, timeSelectorDialog, additionalRenderers) {
    AbstractResourceController.call(this);
    const self = this;

    additionalRenderers = additionalRenderers || {};
    const FW_RULES_RESOURCE_TYPE = "firewall_rule";
    const DYN_APPS_RESOURCE_TYPE = "dynamic_app";
    const MAX_TIME = 86399940; // 999 days, 23 hours, 59 minutes
    const MIN_TIME = 60; // 1 minute

    let timeUpdateSubscription = undefined;
    let timeShowSubscription = undefined;
    let updateStateSubscription = undefined;

    self.subscribeToChannel("Electron", "show-dynapps-view", () => {
        self.showView(listView);
    });

    /**
     * @override
     * @param {string} [id] the id of the resource
     */
    this.launchResource = function(id) {
        $(document).one("resourceslistchange", (event, resources) => {
            if ($.isEmptyObject(resources)) {
                return;
            }
            const resource = CudaLaunch.models.resources.getResource(id);
            if (resource === undefined) {
                return;
            }
            launchDialog.initialize(resource);
            updateStateSubscription = self.subscribeTo("changeDynAppState", (data) => {
                self.unsubscribe(updateStateSubscription);
                submitDynAppChange(data);
            });
            self.showView(launchDialog, true);
        });
        CudaLaunch.models.resources.refresh();
    };

    /**
     * @override
     * @param {string} [id] the id of the resource
     */
    this.checkForPrerequisites = function(id) {
        self.launchResource(id);
    };

    const listView = new DynAppsListView(this.checkForPrerequisites);

    this.setResourcesView(listView);

    this.addDelegateRenderingController = function(controller) {
        if (controller instanceof AbstractResourceController) {
            const renderers = controller.getResourcesView().getRenderers();
            Object.keys(renderers).forEach((key) => {
                listView.addRenderer(key, renderers[key]);
            });
        }
    };
    Object.keys(additionalRenderers).forEach((key) => {
        listView.addRenderer(key, additionalRenderers[key]);
    });
    listView.addRenderer(FW_RULES_RESOURCE_TYPE, new DynAppsRenderer());
    listView.addRenderer(DYN_APPS_RESOURCE_TYPE, new DynAppsRenderer());

    /**
     * @override
     * @inheritDoc
     */
    this.getViews = function() {
        return [listView, launchDialog, timeSelectorDialog];
    };

    /**
     * @override
     * @inheritDoc
     */
    this.getResourceTypes = function() {
        return [FW_RULES_RESOURCE_TYPE, DYN_APPS_RESOURCE_TYPE];
    };

    /**
     * @override
     * @inheritsDoc
     */
    this.getId = function() {
        return "DynAppsController";
    };

    function submitDynAppChange(data) {
        const dynApp = data.dynapp;
        dynApp.state = data.newState;

        if (data.hasOwnProperty("newTimeRemaining")) {
            if (data.newTimeRemaining === 0) {
                dynApp.state = "Disabled";
                dynApp.time_left = -1;
            } else {
                dynApp.time_left = Math.floor(data.newTimeRemaining / 60); // API expects time to be in minutes
            }
        } else {
            dynApp.time_left = -1;
        }

        let ruleId = dynApp.id;

        // Strip off the resource_type prefix
        if (dynApp.id.indexOf(dynApp.type) === 0) {
            ruleId = dynApp.id.substring(dynApp.type.length + 1);
        }

        const params = {
            id : ruleId,
            ruleset : dynApp.ruleset,
            state : dynApp.state,
            time_left : dynApp.time_left
        };

        const updateReqParams = {
            type : "POST",
            data : params,
            async : true,
            cache : false
        };

        if (dynApp.ruleset) {
            updateReqParams.url = "/sslvpn_api/resource/rulesets/" + dynApp.ruleset + "/rules/" + ruleId;
        } else {
            updateReqParams.url = "/sslvpn_api/resource/dynapps/" + ruleId;
        }

        $.ajax(updateReqParams).always(() => {
            // Always refresh the resources as it might be that the rule we tried to update no longer exists
            CudaLaunch.models.resources.refresh();
        }).done(() => {
            console.info("DynApp '" + dynApp.id + "' updated");
        }).fail((jqXHR) => {
            if (jqXHR.status === 403) {
                console.error("Forbidden to update DynApp '" + dynApp.id + "'");
                CudaLaunch.controllers.RootController.showErrorDialog($.t("common::dynapps.forbidden"));
            } else {
                console.error("Unable to update DynApp '" + dynApp.id + "'");
                CudaLaunch.controllers.RootController.showErrorDialog($.t("common::errorPage.message"));
            }
        });
    }

    self.subscribeTo("show-time-selector", (data) => {
        self.unsubscribe(updateStateSubscription);
        timeUpdateSubscription = self.subscribeTo("time-updated", (newTime) => {
            self.unsubscribe(timeUpdateSubscription);
            submitDynAppChange({
                dynapp : data.dynapp,
                newState : "Enabled",
                newTimeRemaining : newTime.time
            });
        });

        let maxTime = MAX_TIME;
        let minTime = MIN_TIME;

        if (data.dynapp.hasOwnProperty("min_time") && data.dynapp.min_time > 0) {
            minTime = data.dynapp.min_time;
        }

        if (data.dynapp.hasOwnProperty("max_time") && data.dynapp.max_time > 0) {
            maxTime = data.dynapp.max_time;
        }
        timeSelectorDialog.initialize(minTime, maxTime);
        self.showView(timeSelectorDialog, true);

        // Update the time displayed on the time selector after its been shown so that it can scroll to correct values
        timeShowSubscription = self.subscribeTo("time-selector-show", () => {
            self.unsubscribe(timeShowSubscription);
            $(".timeSelector").timeSelector("setTimeComponents", data.time);
        });
    });

    self.subscribeTo("cancel-time-selector", () => {
        self.unsubscribe(timeUpdateSubscription);
        self.unsubscribe(timeShowSubscription);
    });

    self.subscribeTo("cancel-launch-dialog", () => {
        self.unsubscribe(updateStateSubscription);
    });
}

Utils.inherits(DynAppsController, AbstractResourceController);

module.exports = DynAppsController;